import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useFormikContext } from 'formik'
import { KTSVG, toAbsoluteUrl } from '../../../../../_metronic/helpers'
import { getExperiencesByStatus } from '../../../../services/adminExperiencesService'
import { useNearBottom } from '../../../../hooks/utils/useNearBottom'

type Experience = {
  id: number
  title: string
  host?: string
}

type ApiExperience = {
  id: number
  title?: string
  host?: any
}

type Props = {
  values: number[]
  onChange: (val: number[]) => void
  placeholder?: string
  minChars?: number
  name?: string
  // Preloaded experiences to seed the chip cache when opening in edit mode.
  preloaded?: ApiExperience[]
}

const LIMIT = 20

const hostLabel = (host: any): string | undefined => {
  if (!host) return undefined
  if (typeof host === 'string') return host
  const first = (host.firstName || host.first_name || '').trim()
  const last = (host.lastName || host.last_name || '').trim()
  const name = `${first} ${last}`.trim()
  return name || host.email
}

const normalize = (e: ApiExperience): Experience => ({
  id: e.id,
  title: e.title || `#${e.id}`,
  host: hostLabel(e.host),
})

export function SelectExperienceField({
  values,
  onChange,
  placeholder = 'Search experiences…',
  minChars = 0,
  name,
  preloaded,
}: Props) {
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [items, setItems] = useState<Experience[]>([])
  const [count, setCount] = useState(0)
  const [offset, setOffset] = useState(0)
  const [loading, setLoading] = useState(false)
  const [selectedCache, setSelectedCache] = useState<Record<number, Experience>>({})
  const formik = useFormikContext()

  const reqRef = useRef(0)
  const hasMore = items.length < count

  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 300)
    return () => clearTimeout(id)
  }, [query])

  const fetchData = useCallback(async (search: string, nextOffset: number) => {
    const reqId = ++reqRef.current
    setLoading(true)
    try {
      const res: any = await getExperiencesByStatus({ search, limit: LIMIT, offset: nextOffset })
      if (reqId !== reqRef.current) return
      const raw: ApiExperience[] = res?.results ?? []
      const results = raw.map(normalize)
      setCount(Number(res?.count ?? results.length))
      setItems((prev) => (nextOffset === 0 ? results : [...prev, ...results]))
    } catch {
      if (reqId === reqRef.current && nextOffset === 0) {
        setItems([])
        setCount(0)
      }
    } finally {
      if (reqId === reqRef.current) setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (debounced.length < minChars) return
    setOffset(0)
    fetchData(debounced, 0)
  }, [debounced, minChars, fetchData])

  const loadMore = useCallback(() => {
    if (loading || !hasMore) return
    const next = offset + LIMIT
    setOffset(next)
    fetchData(debounced, next)
  }, [loading, hasMore, offset, debounced, fetchData])

  const handleScroll = useNearBottom(loadMore)

  useEffect(() => {
    setSelectedCache((prev) => {
      const nextCache = { ...prev }
      for (const it of items) if ((values ?? []).includes(it.id)) nextCache[it.id] = it
      return nextCache
    })
  }, [items, values])

  useEffect(() => {
    if (!preloaded?.length) return
    setSelectedCache((prev) => {
      const nextCache = { ...prev }
      for (const raw of preloaded) {
        const e = normalize(raw)
        if (!nextCache[e.id]) nextCache[e.id] = e
      }
      return nextCache
    })
  }, [preloaded])

  const highlightMatch = (text: string, q: string) => {
    if (!q) return text
    const regex = new RegExp(`(${q.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi')
    const parts = text.split(regex)
    return (
      <>
        {parts.map((part, i) =>
          regex.test(part) ? (
            <span key={i} style={{ fontWeight: 700 }}>{part}</span>
          ) : (
            <span key={i} style={{ fontWeight: 300 }}>{part}</span>
          )
        )}
      </>
    )
  }

  const handleAdd = (itemId: number) => {
    if (!(values ?? []).includes(itemId)) onChange([...(values ?? []), itemId])
    setQuery('')
    setOpen(false)
  }

  const handleRemove = (itemId: number) => {
    onChange((values ?? []).filter((v) => v !== itemId))
  }

  const validateForm = () => {
    if (name) {
      formik.setFieldTouched(name, true)
      formik.validateForm()
    }
  }

  const selectedObjects = (values ?? [])
    .map((id) => selectedCache[id])
    .filter(Boolean) as Experience[]

  return (
    <div className='nb-language-autocomplete'>
      <div className='nb-language-wrapper'>
        {selectedObjects.map((item) => (
          <div key={item.id} className='nb-admin-team-item-select'>
            <span>{item.title}</span>
            <div onClick={() => handleRemove(item.id)}>
              <KTSVG path={toAbsoluteUrl('/media/svg/nobilis/remove.svg')} />
            </div>
          </div>
        ))}

        <input
          className='tap-add-experience-input-category nb-language-input'
          placeholder={placeholder}
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setOpen(true)
            validateForm()
          }}
          onFocus={() => {
            setOpen(true)
            validateForm()
          }}
          onBlur={() => setTimeout(() => setOpen(false), 120)}
        />
      </div>

      {open && (
        <div className='nb-language-menu' onScroll={handleScroll}>
          {items.length === 0 && !loading && (
            <div className='empty-state'>No experiences found</div>
          )}

          {items.map((item) => (
            <div
              key={item.id}
              className='nb-language-item cursor-pointer'
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleAdd(item.id)}
            >
              {highlightMatch(item.title, debounced)}
              {item.host && (
                <span style={{ color: '#808080', fontSize: 12, marginLeft: 6 }}>· {item.host}</span>
              )}
            </div>
          ))}

          {loading && (
            <div className='nb-language-item' style={{ color: '#808080' }}>
              Loading…
            </div>
          )}
        </div>
      )}
    </div>
  )
}
